/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * Typed EOS constants extracted from constants.js.
 * Provides type-safe access to all EOS-specific constant values
 * used across the Setup Users & Workflows modal flow.
 */

import type {
  EosConfigMethod,
  EosConfigLevel,
  EosWfConfigLevel,
  EosActivityType,
  EosDownloadStatus,
  EosAcknowledgementStatus
} from '../types/eos'

// ---------------------------------------------------------------------------
// EOS Activity Types (used in reports)
// ---------------------------------------------------------------------------

export const EOS_ACTIVITY = {
  SUMMARY: 'SUMMARY' as EosActivityType,
  DETAILED: 'DETAILED' as EosActivityType,
  WORKFLOW: 'WORKFLOW' as EosActivityType,
  USER_ACCESS: 'USER_ACCESS' as EosActivityType,
  DOWNLOAD: 'DOWNLOAD' as EosActivityType,
  ACKNOWLEDGEMENT: 'ACKNOWLEDGEMENT' as EosActivityType
} as const

// ---------------------------------------------------------------------------
// EOS Download Status (used in reports filter)
// ---------------------------------------------------------------------------

export const EOS_DOWNLOAD_STATUS = {
  ALL: 'ALL' as EosDownloadStatus,
  OCCURRED: 'OCCURRED' as EosDownloadStatus,
  NOT_OCCURRED: 'NOT_OCCURRED' as EosDownloadStatus
} as const

// ---------------------------------------------------------------------------
// EOS Acknowledgement Status (used in reports filter)
// ---------------------------------------------------------------------------

export const EOS_ACKNOWLEDGEMENT_STATUS = {
  ALL: 'ALL' as EosAcknowledgementStatus,
  NOT_STARTED: 'NOT_STARTED' as EosAcknowledgementStatus,
  IN_PROGRESS: 'IN_PROGRESS' as EosAcknowledgementStatus,
  COMPLETED: 'COMPLETED' as EosAcknowledgementStatus
} as const

// ---------------------------------------------------------------------------
// EOS Configuration Method
// ---------------------------------------------------------------------------

export const EOS_CONFIG_METHOD = {
  APP: 'app' as EosConfigMethod,
  BULK: 'bulk' as EosConfigMethod
} as const

// ---------------------------------------------------------------------------
// EOS Configuration Level (core constant used throughout the wizard)
// ---------------------------------------------------------------------------

export const EOS_CONFIG_LEVEL = {
  SITE: 'SITE' as EosConfigLevel,
  STUDY: 'STUDY' as EosConfigLevel,
  COUNTRY: 'COUNTRY' as EosConfigLevel
} as const

// ---------------------------------------------------------------------------
// EOS Workflow Configuration Level (display labels)
// ---------------------------------------------------------------------------

export const EOS_WF_CONFIG_LEVEL = {
  STUDY_SITE: 'Study/Site' as EosWfConfigLevel,
  COUNTRY: 'Country' as EosWfConfigLevel
} as const

// ---------------------------------------------------------------------------
// Null UUID sentinel value (used in loadStudyInfo validation)
// ---------------------------------------------------------------------------

export const EOS_NULL_UUID = '00000000-0000-0000-0000-000000000000'

// ---------------------------------------------------------------------------
// Workflow step types relevant to EOS
// ---------------------------------------------------------------------------

export const EOS_WORKFLOW_STEP_TYPE = {
  DOWNLOAD_EOS: 'DOWNLOAD_EOS',
  ACKNOWLEDGE_EOS: 'ACKNOWLEDGE_EOS'
} as const

// ---------------------------------------------------------------------------
// Task statuses relevant to EOS user postprocessing
// ---------------------------------------------------------------------------

export const EOS_TASK_STATUS = {
  APPROVED: 'APPROVED',
  OPEN: 'OPEN',
  CANCELLED: 'CANCELLED',
  INACTIVE: 'INACTIVE'
} as const

// ---------------------------------------------------------------------------
// Workflow status constants
// ---------------------------------------------------------------------------

export const EOS_WORKFLOW_STATUS = {
  ACTIVE: 'ACTIVE',
  FINISHED: 'FINISHED'
} as const

// ---------------------------------------------------------------------------
// User roles relevant to EOS
// ---------------------------------------------------------------------------

export const EOS_USER_ROLES = {
  ADMIN: 'ADMIN',
  CONTENT_ADMIN: 'CONTENT_ADMIN',
  POWER_USER: 'POWER_USER'
} as const

// ---------------------------------------------------------------------------
// Instruction types
// ---------------------------------------------------------------------------

export const EOS_INSTRUCTION_TYPE = {
  SPONSOR: 'SPONSOR'
} as const

export const EOS_INSTRUCTION_STATUS = {
  SAVED: 'SAVED',
  FINALIZED: 'FINALIZED'
} as const
